import React from "react";
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { FormikProps } from "formik";
import {
  DatePicker,
  Form,
  Icon,
  Input as AntInput,
  Select,
  Tooltip
} from "antd";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import moment, { Moment } from "moment";

import globalColors from "../styles/globalColors";
import { DISPLAY_DATE_FORMAT, parseMoney } from "../utils/dataUtils";

const Option = Select.Option;

type Props = FormikProps<any> & {
  birthdate?: boolean;
  blurOnEnter?: boolean;
  containerStyle?: any;
  formItemStyle?: any;
  datePicker?: boolean;
  allowEmptySelectOption?: boolean;
  icon?: any;
  label?: string;
  maxLength?: number;
  name: string;
  onChange?: any;
  phone?: boolean;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  select?: boolean;
  selectKey?: string;
  selectOptions?: Array<any>;
  showSearch?: boolean;
  textArea?: boolean;
  tooltip?: string;
  type?: any;
  value?: any;
  loading?: boolean;
  disabled?: boolean;
  money?: boolean;
  disabledDate?: (current: Moment | undefined | null) => boolean;
};

const Input: React.FC<Props> = ({
  containerStyle,
  formItemStyle,
  birthdate,
  blurOnEnter,
  datePicker,
  allowEmptySelectOption,
  errors,
  handleChange,
  handleBlur,
  icon,
  label,
  maxLength,
  name,
  onChange,
  placeholder,
  phone,
  required,
  rows,
  select,
  selectKey,
  selectOptions,
  setFieldTouched,
  setFieldValue,
  showSearch = true,
  textArea,
  tooltip,
  touched,
  type,
  value,
  values,
  loading,
  disabled,
  money,
  disabledDate
}) => {
  const touchedForField = name.split(".").reduce((touch: any, prop: string) => {
    return touch && touch[prop];
  }, touched);

  const errorForField = name.split(".").reduce((error: any, prop: string) => {
    return error && error[prop];
  }, errors);

  const hasError = !!(touchedForField && errorForField);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (blurOnEnter && e.key === "Enter") {
      // @ts-ignore
      e.preventDefault();
      // @ts-ignore
      e.target.blur();
    }
  }

  const InputComponent: any = textArea ? AntInput.TextArea : AntInput;

  const inputValue = value || values[name] || "";

  const phoneMask = [
    "(",
    /\d/,
    /\d/,
    /\d/,
    ")",
    " ",
    /\d/,
    /\d/,
    /\d/,
    "-",
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ];
  const birthdateMask = [
    /\d/,
    /\d/,
    "-",
    /\d/,
    /\d/,
    "-",
    /\d/,
    /\d/,
    /\d/,
    /\d/
  ];
  const moneyMask = createNumberMask({
    prefix: "$",
    allowDecimal: true
  });
  
  let optionsForSelect =
    selectOptions &&
    selectOptions.map((option: any) => (
      <Option key={option[selectKey!]} value={option[selectKey!]}>
        {option.name}
      </Option>
    ));

  if (optionsForSelect && allowEmptySelectOption) {
    optionsForSelect = [
      <Option key={""} value={""}>
        Select
      </Option>,
      ...optionsForSelect
    ];
  }

  return (
    <div css={containerStyle}>
      {label ? (
        tooltip ? (
          <Tooltip title={tooltip}>
            <label htmlFor={name}>
              {label} {required && <span css={styles.requiredStar}>*</span>}
            </label>
          </Tooltip>
        ) : (
          <label htmlFor={name}>
            {label} {required && <span css={styles.requiredStar}>*</span>}
          </label>
        )
      ) : null}
      <Form.Item
        validateStatus={hasError ? "error" : undefined}
        help={hasError ? errorForField : null}
        style={formItemStyle}
      >
        {select && selectOptions ? (
          <Select
            css={[styles.fullWidth, required && styles.required]}
            placeholder={placeholder}
            value={inputValue}
            showSearch={showSearch}
            filterOption={(input, option) => {
              return (
                (option!.props!.children! as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              );
            }}
            size="large"
            // showAction={["click", "focus"]}
            onBlur={() => setTimeout(() => setFieldTouched(name))}
            loading={loading}
            disabled={disabled}
            onChange={
              onChange
                ? onChange
                : (value: string) => {
                    setFieldValue(name, value);
                    setFieldTouched(name);
                  }
            }
          >
            {optionsForSelect}
          </Select>
        ) : datePicker ? (
          <DatePicker
            css={[styles.fullWidth, required && styles.required]}
            format={DISPLAY_DATE_FORMAT}
            onOpenChange={status => {
              if (!status) {
                setFieldTouched(name);
              }
            }}
            onChange={
              onChange
                ? onChange
                : (date: any, _dateString: string) => {
                    setFieldValue(name, date);
                    setFieldTouched(name);
                  }
            }
            size="large"
            value={inputValue ? moment(inputValue) : undefined}
            disabledDate={disabledDate}
          />
        ) : birthdate ? (
          <MaskedInput
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            value={inputValue}
            mask={birthdateMask}
            showMask={true}
            guide={true}
            className="ant-input ant-input-lg"
            style={(required && styles.requiredForMask) || undefined}
          />
        ) : phone ? (
          <MaskedInput
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            value={inputValue}
            mask={phoneMask}
            showMask={true}
            guide={false}
            className="ant-input ant-input-lg"
            style={(required && styles.requiredForMask) || undefined}
          />
        ) : money ? (
          <MaskedInput
            name={name}
            onChange={(e: any) => onChange(parseMoney(e.currentTarget.value))}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            value={inputValue}
            mask={moneyMask}
            showMask={true}
            guide={false}
            className="ant-input ant-input-lg"
            style={(required && styles.requiredForMask) || undefined}
          />
        ) : (
          <InputComponent
            size="large"
            maxLength={maxLength}
            name={name}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            rows={rows}
            type={type}
            placeholder={placeholder}
            loading={loading}
            disabled={disabled}
            prefix={
              icon ? (
                <Icon type={icon} style={{ color: "rgba(0,0,0,.25)" }} />
              ) : null
            }
            css={required && styles.required}
            value={inputValue}
          />
        )}
      </Form.Item>
    </div>
  );
};

const styles = {
  error: {
    color: globalColors.red
  },
  fullWidth: {
    width: "100%",
    ".ant-select-selection:focus, .ant-select-selection:hover, .ant-select-selection:active": {
      borderColor: globalColors.secondary
    }
  },
  required: {
    "input, .ant-select-selection": {
      borderColor: globalColors.orange
    }
  },
  requiredForMask: {
    borderColor: globalColors.orange
  },
  requiredStar: {
    color: globalColors.red
  }
};

export default Input;
